import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { RefreshCw } from "lucide-react";
import api from "../lib/api";
import { siteConfig } from "../lib/siteConfig";

export default function PlayerRegionGate({ children }) {
  const { pathname, search } = useLocation();
  const admin = pathname === "/admin" || pathname.startsWith("/admin/");
  const adminToken = localStorage.getItem("aurum_admin_token");
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator?.standalone === true || window.Capacitor?.isNativePlatform?.()
    || /; wv\)/.test(window.navigator.userAgent);
  const app = new URLSearchParams(search).get("app");
  const adminLaunch = pathname === "/" && app !== "fea-slots" && Boolean(adminToken
    || app === "fea-slots-admin" || (standalone && localStorage.getItem("fea_admin_app") === "true"));
  const [result, setResult] = useState({ path: "", status: "checking" });
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    document.querySelector('link[rel="manifest"]')?.setAttribute("href", admin || adminLaunch ? "/admin-manifest.json" : "/manifest.json");
    if (adminLaunch) {
      window.location.replace(adminToken ? "/admin/dashboard" : "/admin");
      return undefined;
    }
    if (admin) {
      localStorage.setItem("fea_admin_app", "true");
      setResult({ path: "", status: "checking" });
      return undefined;
    }
    let active = true;
    let controller;
    const check = async () => {
      controller?.abort();
      controller = new AbortController();
      try {
        const { data } = await api.get("/player-region", { signal: controller.signal, timeout: 10000, skipMaintenance: true });
        if (active) setResult({ path: pathname, status: data?.blocked === false ? "allowed" : "blocked" });
      } catch (error) {
        if (active && error.code !== "ERR_CANCELED") setResult({ path: pathname, status: "error" });
      }
    };
    check();
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    return () => {
      active = false;
      controller?.abort();
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
    };
  }, [admin, adminLaunch, adminToken, pathname, retry]);

  if (admin) return children;
  if (adminLaunch) return null;
  const status = result.path === pathname ? result.status : "checking";
  if (status === "allowed") return children;
  return (
    <main className="min-h-screen bg-[#0A0A0B] px-6 py-16 text-center text-white" data-testid="player-region-gate">
      <img src={siteConfig.logoUrl} alt={siteConfig.name} className="mx-auto mb-6 h-16 w-16 rounded-full" />
      <h1 className="text-2xl font-bold">{siteConfig.name}</h1>
      <p className="mx-auto mt-4 max-w-md text-base text-slate-300" role="status">
        {status === "checking" ? "Checking availability..." : status === "blocked"
          ? "Player access is unavailable from your current location."
          : "Unable to check availability. Please try again."}
      </p>
      {status === "error" && (
        <button type="button" onClick={() => setRetry((value) => value + 1)}
          className="mx-auto mt-6 flex items-center gap-2 rounded-md bg-[#2F6FA5] px-4 py-3 font-semibold">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
      )}
    </main>
  );
}
